import { Icon as UiIcon } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import type Decimal from "decimal.js";
import { useTranslation } from "react-i18next";
import Select, {
  components,
  type DropdownIndicatorProps,
  type SingleValue,
} from "react-select";
import type { CurrencyPrimitive } from "@/api/cashier";
import globe from "@/assets/icons/globe-icon.svg";
import { Icon } from "@/components/general/icon/Icon";
import type { EIcon } from "@/utils/enums";
import styles from "./CurrencyConversionDropdown.module.css";

export type CurrencyConversionOption = {
  code: string;
  convertedAmount?: Decimal;
  rate?: Decimal;
};

type CurrencyConversionDropdownProps = {
  options: CurrencyConversionOption[];
  value: string | null;
  onChange: (code: string) => void;
  tradingCurrencyCode: string;
  methodName: string;
  currencies: Record<string, CurrencyPrimitive>;
};

type SelectOption = {
  value: string;
  label: string;
  rate?: Decimal;
  convertedAmount?: Decimal;
  currency?: CurrencyPrimitive;
};

const DropdownIndicator = (
  props: DropdownIndicatorProps<SelectOption, false>
) => (
  <components.DropdownIndicator {...props}>
    <div
      className={clsx(
        styles.arrow,
        props.selectProps.menuIsOpen && styles.arrowOpen
      )}
    >
      <UiIcon icon="arrow-down" size={16} />
    </div>
  </components.DropdownIndicator>
);

export const CurrencyConversionDropdown = ({
  options,
  value,
  onChange,
  tradingCurrencyCode,
  methodName,
  currencies,
}: CurrencyConversionDropdownProps) => {
  const { t } = useTranslation();

  const inputId = `currency-conversion-select-${methodName.toLowerCase().replace(/\s+/g, "-")}`;

  const selectOptions: SelectOption[] = options.map((opt) => ({
    value: opt.code,
    label: opt.code,
    rate: opt.rate,
    convertedAmount: opt.convertedAmount,
    currency: currencies[opt.code],
  }));

  const selectedOption = selectOptions.find((o) => o.value === value) ?? null;

  return (
    <div className={styles.wrapper} data-testid="currency-conversion-dropdown">
      <label htmlFor={inputId} className={styles.label}>
        {t("withdrawals.methods.currency_conversion.receiving_currency_in")}
      </label>
      <div className={styles.notice}>
        {t("withdrawals.methods.currency_conversion.not_supported", {
          currency: tradingCurrencyCode,
          methodName,
        })}
      </div>
      <Select<SelectOption, false>
        inputId={inputId}
        classNames={{
          control: ({ menuIsOpen }) =>
            clsx(styles.control, menuIsOpen && styles.controlOpen),
          singleValue: () => styles.singleValue,
          placeholder: () => styles.placeholder,
          indicatorSeparator: () => styles.indicatorSeparator,
          menu: () => styles.menu,
          menuList: () => styles.menuList,
          option: ({ isSelected }) =>
            clsx(styles.option, isSelected && styles.optionSelected),
        }}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator,
        }}
        value={selectedOption}
        onChange={(newValue: SingleValue<SelectOption>) => {
          if (newValue) onChange(newValue.value);
        }}
        options={selectOptions}
        placeholder={t(
          "withdrawals.methods.currency_conversion.select_currency"
        )}
        isClearable={false}
        isSearchable={false}
        data-testid="currency-conversion-dropdown-select"
        formatOptionLabel={(option, { context }) => {
          if (context === "value") {
            return (
              <span className={styles.singleValueText}>{option.label}</span>
            );
          }
          return (
            <div
              className={clsx(
                styles.optionContent,
                option.value === value && styles.optionContentSelected
              )}
            >
              {option.currency && (
                <Icon
                  name={
                    `flags/${option.currency.code?.toLocaleLowerCase()}-flag` as EIcon
                  }
                  fallback={globe}
                />
              )}
              <span className={styles.optionLabel}>
                <span>
                  {option.label}
                  {option.convertedAmount
                    ? ` (≈ ${option.convertedAmount
                        .toNumber()
                        .toLocaleString("en", {
                          minimumFractionDigits: option.currency?.decimals ?? 2,
                          maximumFractionDigits: option.currency?.decimals ?? 2,
                          useGrouping: true,
                        })} ${option.label})`
                    : ""}
                </span>
                {option.rate && (
                  <span className={styles.optionMinimum}>
                    1 {tradingCurrencyCode} ={" "}
                    {option.rate.toNumber().toLocaleString("en", {
                      minimumFractionDigits:
                        option.currency?.decimals === 0 ? 0 : 4,
                      maximumFractionDigits:
                        option.currency?.decimals === 0 ? 0 : 4,
                      useGrouping: false,
                    })}{" "}
                    {option.label}
                  </span>
                )}
              </span>
              {option.value === value && <UiIcon icon="check-mark" size={16} />}
            </div>
          );
        }}
      />
    </div>
  );
};
